import React, { useCallback, useContext, useMemo, useState } from "react";
import { Menu } from "@design-system/Menu/Menu.lib";
import { isAxiosError } from "axios";
import { Counterparty } from "@common/types/counterparty";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { createDefaultCounterpartyDrillDownTransactionView } from "@components/transactions/views/DefaultCounterpartyDrillDownTransactionView";
import {
    useCounterpartyDrillDownAvailableFilters,
    useCounterpartyTransactionsFilters,
} from "@hooks/useCounterpartyTransactionsOpener";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { Loader } from "@design-system/Loader/Loader";
import {
    isCounterpartyBillOrInvoiceLinksError,
    isCounterpartyLockedTransactionsError,
    useCounterpartyRemovalMutation,
    useUpdateCounterpartyMutation,
} from "@api/counterparty.api";
import { StandardModal } from "@design-system/Modal/Modal";
import { CounterpartyLinkedToBillsOrInvoicesModal } from "./CounterpartyLinkedToBillsOrInvoicesModal";
import { DirectoryContext } from "./Directory.context";
import { DisableCounterpartyInsteadModal } from "./DisableCounterpartyInsteadModal";
import { MergeOrRemoveCounterpartyModal } from "./MergeOrRemoveCounterpartyModal";
import { MergeCounterpartyModal } from "./MergeCounterpartyModal";

interface Props {
    counterparty: Counterparty;
    className: string;
    journalEntryLinesCount?: number;
    /** True when the counterparty is linked to any bill or invoice, so
     * deletion is disallowed and the row action should read "Archive". */
    hasBillOrInvoiceLinks?: boolean;
}

export const CounterpartyManagementDropdown: React.FC<Props> = ({
    counterparty,
    className,
    journalEntryLinesCount,
    hasBillOrInvoiceLinks,
}) => {
    const openGlobalTransactionViewDrawer = useGlobalTransactionViewDrawer();
    const availableFilters = useCounterpartyDrillDownAvailableFilters();
    const { setEditedItem, openGlDrawer } = useContext(DirectoryContext);
    const transactionsFilters = useCounterpartyTransactionsFilters(
        counterparty.id,
        {
            includeDateFilter: true,
        },
    );
    const isClassesEnabled = useIsClassesEnabled();
    const defaultCounterpartyDrillDownTransactionView = useMemo(
        () =>
            createDefaultCounterpartyDrillDownTransactionView({
                isClassesEnabled,
            }),
        [isClassesEnabled],
    );
    const counterpartyRemoval = useCounterpartyRemovalMutation(counterparty);
    const counterpartyUpdate = useUpdateCounterpartyMutation(counterparty);
    const [showMergeOrRemoveModal, setShowMergeOrRemoveModal] = useState(false);
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [showDisableInsteadModal, setShowDisableInsteadModal] =
        useState(false);
    const [showBillOrInvoiceLinksModal, setShowBillOrInvoiceLinksModal] =
        useState(false);

    const isDisabled = Boolean(counterparty.disabledAt);
    const shouldOfferArchiveInsteadOfDelete = Boolean(
        hasBillOrInvoiceLinks && counterparty.workspaceId,
    );
    const removeMenuItemLabel = shouldOfferArchiveInsteadOfDelete
        ? "Archive"
        : "Delete";
    const onEnable = useCallback(async () => {
        await counterpartyUpdate.mutateAsync({ isDisabled: false });
    }, [counterpartyUpdate]);

    const onRemove = useCallback(async () => {
        // Proactively surface the archive-instead flow when usage stats
        // already show bill/invoice links, avoiding an unnecessary round trip
        // to the (still authoritative) backend check.
        if (hasBillOrInvoiceLinks && counterparty.workspaceId) {
            setShowBillOrInvoiceLinksModal(true);
            return;
        }

        if (journalEntryLinesCount) {
            setShowMergeOrRemoveModal(true);
            return;
        }

        try {
            await counterpartyRemoval.mutateAsync();
        } catch (e) {
            if (isAxiosError(e) && e.response?.data?.transactionsFound) {
                setShowMergeOrRemoveModal(true);
            } else if (
                isCounterpartyLockedTransactionsError(e) &&
                counterparty.workspaceId
            ) {
                setShowDisableInsteadModal(true);
            } else if (
                isCounterpartyBillOrInvoiceLinksError(e) &&
                counterparty.workspaceId
            ) {
                setShowBillOrInvoiceLinksModal(true);
            } else {
                throw e;
            }
        }
    }, [
        hasBillOrInvoiceLinks,
        journalEntryLinesCount,
        counterpartyRemoval,
        counterparty.workspaceId,
    ]);

    const onDisableInstead = useCallback(async () => {
        await counterpartyRemoval.mutateAsync({
            force: true,
            disableInsteadOfDelete: true,
        });
        setShowDisableInsteadModal(false);
    }, [counterpartyRemoval]);

    const onArchiveInstead = useCallback(async () => {
        await counterpartyRemoval.mutateAsync({
            force: true,
            disableInsteadOfDelete: true,
        });
        setShowBillOrInvoiceLinksModal(false);
    }, [counterpartyRemoval]);

    return (
        <>
            <Menu.More className={className}>
                <Menu.Item onClick={() => setEditedItem(counterparty)}>
                    Edit
                </Menu.Item>
                <Menu.Item
                    onClick={() =>
                        openGlobalTransactionViewDrawer(transactionsFilters, {
                            baseViewOverride:
                                defaultCounterpartyDrillDownTransactionView,
                            availableFilters,
                        })
                    }
                >
                    View transactions
                </Menu.Item>
                <Menu.Item
                    onClick={() => openGlDrawer(counterparty.id, false)}
                    disabled={!journalEntryLinesCount}
                >
                    View GL
                </Menu.Item>
                <Menu.Item onClick={() => setShowMergeModal(true)}>
                    Merge
                </Menu.Item>
                {counterparty.workspaceId && isDisabled && (
                    <Menu.Item onClick={onEnable}>
                        {counterpartyUpdate.isPending ? (
                            <Loader inline />
                        ) : (
                            "Enable"
                        )}
                    </Menu.Item>
                )}
                <Menu.Item onClick={onRemove} destructive>
                    {counterpartyRemoval.isPending ? (
                        <Loader inline />
                    ) : (
                        removeMenuItemLabel
                    )}
                </Menu.Item>
            </Menu.More>
            <StandardModal
                show={showMergeOrRemoveModal}
                onHide={() => setShowMergeOrRemoveModal(false)}
            >
                <MergeOrRemoveCounterpartyModal
                    counterparty={counterparty}
                    journalEntryLinesCount={journalEntryLinesCount}
                    close={() => setShowMergeOrRemoveModal(false)}
                />
            </StandardModal>
            <StandardModal
                show={showMergeModal}
                onHide={() => setShowMergeModal(false)}
            >
                <MergeCounterpartyModal
                    counterparty={counterparty}
                    journalEntryLinesCount={journalEntryLinesCount}
                    close={() => setShowMergeModal(false)}
                />
            </StandardModal>
            <DisableCounterpartyInsteadModal
                show={showDisableInsteadModal}
                counterparty={counterparty}
                onConfirm={onDisableInstead}
                onHide={() => setShowDisableInsteadModal(false)}
            />
            <CounterpartyLinkedToBillsOrInvoicesModal
                show={showBillOrInvoiceLinksModal}
                counterparty={counterparty}
                onConfirm={onArchiveInstead}
                onHide={() => setShowBillOrInvoiceLinksModal(false)}
            />
        </>
    );
};
